import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  parseIncompletePhoneNumber,
  parsePhoneNumber as parseCompletePhoneNumber,
} from 'libphonenumber-js';
import * as yup from 'yup';

import type { JoinFormData } from '../utils';

export const useJoinForm = (config: JoinFormConfig) => {
  const { formatMessage } = useIntl();

  const requiredFirstNameMessage = formatMessage(
    messages.formFirstNameRequired,
  );
  const requiredLastNameMessage = formatMessage(messages.formLastNameRequired);
  const requiredEmailMessage = formatMessage(messages.formEmailRequired);
  const requiredPhoneNumberMessage = formatMessage(
    messages.formPhoneNumberRequired,
  );
  const requiredPasswordMessage = formatMessage(messages.formPasswordRequired);
  const requiredZipCodeMessage = formatMessage(messages.formZipCodeRequired);
  const requiredBirthdayMessage = formatMessage(messages.formBirthdayRequired);
  const invalidPhoneNumberMessage = formatMessage(
    messages.formValidationPhoneNumber,
  );
  const invalidPasswordLengthMessage = formatMessage(
    messages.formMinPasswordLength,
  );
  const invalidCheckboxValue = formatMessage(messages.formValidationCheckbox);
  const invalidEmail = formatMessage(messages.formValidationEmail);

  const firstName = yup
    .string()
    .required(requiredFirstNameMessage)
    .max(
      50,
      formatMessage(messages.formMaxNameLength, {
        field_name: 'First',
      }),
    )
    .matches(
      /^[\sA-Za-z]+$/,
      formatMessage(messages.formInvalidCharacters, { field_name: 'First' }),
    );

  const lastName = yup
    .string()
    .required(requiredLastNameMessage)
    .max(
      50,
      formatMessage(messages.formMaxNameLength, {
        field_name: 'Last',
      }),
    )
    .matches(
      /^[\sA-Za-z]+$/,
      formatMessage(messages.formInvalidCharacters, { field_name: 'Last' }),
    );

  const email = yup.string().email(invalidEmail).required(requiredEmailMessage);

  const phoneNumber = yup
    .string()
    .required(requiredPhoneNumberMessage)
    .test('test-valid-phone-number', function (number) {
      const { path, createError } = this;
      const parsedPhoneNumber = parseIncompletePhoneNumber(number ?? '');
      const isValidNumber =
        parsedPhoneNumber.length === US_PHONE_NUMBER_MAX_LENGTH
          ? parseCompletePhoneNumber(
              parsedPhoneNumber,
              US_COUNTRY_CODE,
            ).isValid()
          : true;

      return (
        isValidNumber ||
        createError({ path, message: invalidPhoneNumberMessage })
      );
    })
    .matches(/^\d+$/, invalidPhoneNumberMessage)
    .min(10, invalidPhoneNumberMessage)
    .max(10, invalidPhoneNumberMessage);

  const password = yup
    .string()
    .required(requiredPasswordMessage)
    .min(8, invalidPasswordLengthMessage);

  const zipCode = yup
    .string()
    .required(requiredZipCodeMessage)
    .min(3, formatMessage(messages.formValidationZipCode))
    .max(8, formatMessage(messages.formValidationZipCode));

  const birthday = yup.string().required(requiredBirthdayMessage);

  const terms = yup.bool().oneOf([true], invalidCheckboxValue);

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          firstName,
          lastName,
          email,
          phoneNumber,
          ...(config.hasZipCode ? { zipCode } : {}),
          ...(config.hasPassword ? { password } : {}),
          ...(config.isBirthdayRequired ? { birthday } : {}),
          terms,
        })
        .required(),
    [
      firstName,
      lastName,
      email,
      phoneNumber,
      zipCode,
      config.hasZipCode,
      config.hasPassword,
      config.isBirthdayRequired,
      password,
      birthday,
      terms,
    ],
  );

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<JoinFormData>({
    defaultValues: {
      firstName: config.firstName ?? '',
      lastName: config.lastName ?? '',
      email: config.email ?? '',
      phoneNumber: config.phoneNumber ?? '',
      zipCode: config.zipCode ?? '',
      password: config.password ?? '',
      birthday: config.birthday ?? '',
      terms: false,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  // ────────────────────────────────────────────────────────────────────

  return { form };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  formFirstNameRequired: {
    defaultMessage: 'First name is required',
    description: 'Form | First Name Required',
  },
  formLastNameRequired: {
    defaultMessage: 'Last name is required',
    description: 'Form | Last Name Required',
  },
  formEmailRequired: {
    defaultMessage: 'Email is required',
    description: 'Form | Email Required',
  },
  formPhoneNumberRequired: {
    defaultMessage: 'Phone number is required',
    description: 'Form | Phone Number Required',
  },
  formPasswordRequired: {
    defaultMessage: 'Password is required',
    description: 'Form | Password Required',
  },
  formZipCodeRequired: {
    defaultMessage: 'Zip code is required',
    description: 'Form | Zip Code Required',
  },
  formBirthdayRequired: {
    defaultMessage: 'Birthday is required',
    description: 'Form | Birthday Required',
  },
  formValidationPhoneNumber: {
    defaultMessage: 'Please enter a valid phone number (10 digits)',
    description: 'Form | Validation Phone Number',
  },
  formMinPasswordLength: {
    defaultMessage: 'Password should be at least 8 characters',
    description: 'Form | Min Password Length',
  },
  formValidationCheckbox: {
    defaultMessage: 'Field must be checked',
    description: 'Form | Validation Checkbox',
  },
  formValidationEmail: {
    defaultMessage: 'Must be a valid email',
    description: 'Form | Validation Email',
  },
  formMaxNameLength: {
    defaultMessage: '{field_name} name should be no more than 50 characters',
    description: 'Form | Max Name Length',
  },
  formInvalidCharacters: {
    defaultMessage: '{field_name} name has invalid characters',
    description: 'Form | Invalid Characters',
  },
  formValidationZipCode: {
    defaultMessage: 'Zip code is invalid',
    description: 'Form | Validation Zip Code',
  },
});

// ─── Types ────────────────────────────────────────────────────────────

type JoinFormConfig = Partial<JoinFormData> & {
  isBirthdayRequired: boolean;
  hasPassword: boolean;
  hasZipCode: boolean;
};

// ─── Constants ────────────────────────────────────────────────────────

const US_PHONE_NUMBER_MAX_LENGTH = 10;
const US_COUNTRY_CODE = 'US';
